import { InvoiceSearchParams } from "@portal/api/invoice-list";
import { useInvoiceStatusesConfig } from "@portal/Components/Portal/PotalTabs/Invoice/useInvoiceStatusesConfig";
import { Icon } from "@portal/lib/icons/icons";
import { useInvoiceSearchQuery } from "@portal/queries/invoice-query";
import { Invoice } from "@portal/types/entities/invoice";
import { debounce, filter, isEqual, some, uniqBy } from "lodash";
import { useMemo, useState } from "react";
import { Option } from "../Select/AntSelect";
import {
  MaxTagRenderer,
  TagRendererParams,
  onSelectChange,
} from "../Select/type";
import { StyledTag } from "../Tag/style";
import { ToolTip } from "../ToolTip/AntTooltip";
import { TypographyText } from "../Typography/AntTypography";
import { InvoicesOptions } from "./InvoicesOptions";
import { StyledInvoiceSearch } from "./style";
import { InvoiceOptions, InvoiceSearchProps } from "./type";

const t = {
  placeholder: "Search Invoices....",
};

const invoiceOptionsPredicate = (props: InvoiceOptions) => (
  <Option key={props.details.id} label={props.label} value={props.value}>
    <InvoicesOptions {...props} />
  </Option>
);

const mapInvoiceOptionsPredicate = (invoice: Invoice) => {
  return {
    value: invoice.id,
    label: invoice.number,
    details: invoice,
  };
};

export const mapSelectedValuesPredicate = (invoice: Invoice) => {
  return invoice.id;
};

const CloseIcon = <Icon color="var(--gray-7)" icon={["fal", "times"]} />;
function InvoicesSearch(props: Readonly<InvoiceSearchProps>) {
  const {
    initialValue: defaultValue,
    searchDelay = 300,
    selectedValuesCallBack,
    invoices: defaultInvoices,
    openInvoicesOnly,
  } = props;

  const invoiceStatusesConfig = useInvoiceStatusesConfig();
  const invoiceSearch = useInvoiceSearchQuery();
  const [computedOptions, setComputedOptions] = useState<
    InvoiceOptions[] | undefined
  >(initializeComputedOptions);
  const [selectedValues, setSelectedValues] = useState<Invoice[] | undefined>(
    initializeSelectedValues
  );

  function initializeSelectedValues() {
    return defaultValue;
  }

  function initializeComputedOptions() {
    const isExist = some(defaultInvoices, (item) =>
      isEqual(item, defaultValue?.[0])
    );

    return isExist
      ? defaultInvoices?.map(mapInvoiceOptionsPredicate)
      : uniqBy([...(defaultInvoices ?? []), ...(defaultValue ?? [])], "id").map(
          mapInvoiceOptionsPredicate
        );
  }

  function handleSearch(searchText: string) {
    const params: InvoiceSearchParams = {
      searchText,
      status: openInvoicesOnly ? invoiceStatusesConfig.OPEN : undefined,
    };

    invoiceSearch.mutateAsync({ ...params }).then((data) => {
      const options = [
        ...data.list.map(mapInvoiceOptionsPredicate),
        ...(selectedValues ?? []).map(mapInvoiceOptionsPredicate),
      ];
      setComputedOptions(uniqBy(options, "value"));
    });
  }

  function handleDropdownVisibleChange(visible: boolean) {
    if (!visible) {
      if (selectedValues?.length) {
        const options = uniqBy(
          [...selectedValues, ...(defaultInvoices ?? [])],
          "id"
        ).map(mapInvoiceOptionsPredicate);
        setComputedOptions(options);
      } else {
        setComputedOptions(defaultInvoices?.map(mapInvoiceOptionsPredicate));
      }
    }
  }
  function customTagRender(props: TagRendererParams<InvoiceOptions>[number]) {
    const { label, onClose, closable } = props;

    return (
      <StyledTag closeIcon={CloseIcon} onClose={onClose} closable={closable}>
        {label}
      </StyledTag>
    );
  }
  function maxTagRenderer(props: MaxTagRenderer<string[]>[0]) {
    const omittedValues = props.map((value) => value.label).join(", ");
    return (
      <ToolTip
        placement="bottomLeft"
        title={omittedValues}
        getPopupContainer={(trigger) => {
          return trigger?.parentElement as HTMLElement;
        }}
      >
        <TypographyText fs={"12"}>{`+${props.length}`}</TypographyText>
      </ToolTip>
    );
  }

  const searchCallBack = debounce(handleSearch, searchDelay);

  function handleChange(...args: onSelectChange<string[]>) {
    const [value] = args;

    function filterPredicate(option: InvoiceOptions) {
      return value.includes(option.value);
    }
    function mapDetailsPredicate(option: InvoiceOptions) {
      return option.details;
    }

    const options = filter(computedOptions, filterPredicate).map(
      mapDetailsPredicate
    );

    setSelectedValues(options);
    selectedValuesCallBack?.(value, options);
  }

  // const notfoundContent = loading ? <Spin size="small" /> : null;

  // function handleFilterOptions() {
  //   const filterOptionPredicate = (value: InvoiceOptions) => {
  //     return !selectedValues?.includes(value.details);
  //   };

  //   return filter(computedOptions, filterOptionPredicate);
  // }

  // const options = useMemo(handleFilterOptions, [
  //   computedOptions,
  //   selectedValues,
  // ]);

  const initialValues = useMemo(() => {
    return defaultValue
      ?.map(mapInvoiceOptionsPredicate)
      .map((option) => option.value);
  }, [defaultValue]);

  // const EmptyContent = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <StyledInvoiceSearch
      filterOption={false}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      showSearch
      defaultValue={initialValues}
      mode="multiple"
      placeholder={t.placeholder}
      onChange={handleChange}
      onSearch={searchCallBack}
      optionLabelProp="label"
      tagRender={customTagRender}
      maxTagCount="responsive"
      loading={invoiceSearch.isLoading}
      virtual={false}
      listHeight={250}
      maxTagPlaceholder={maxTagRenderer}
      getPopupContainer={(node) => {
        return node.parentNode as HTMLElement;
      }}
    >
      {computedOptions?.map(invoiceOptionsPredicate)}
    </StyledInvoiceSearch>
  );
}

export default InvoicesSearch;
